import { type FC, useEffect, useRef, useState } from 'react';
import { TypeAnimation } from 'react-type-animation';
import cn from 'classnames';
import { motion } from 'framer-motion';

import { type IntroStepsConfigValue } from '@/common/config/intro-steps';
import { playSound } from '@/common/lib/soundEffect';
import { RoundedButton } from '@/components/ui/Button/Button';

import CharacterPicture from '../Intro/Step/CharacterPicture';

import { tutorialCharacter } from './config';

import overlayCss from '../Intro/Overlay/styles.module.scss';
import stepCss from '../Intro/Step/styles.module.scss';

interface CharacterDialogProps {
  text: string;
  title?: string;
  buttonText?: string;
  onClose: () => void;
}

const textSpeed = 80;

const CharacterDialog: FC<CharacterDialogProps> = ({
  text,
  title,
  buttonText = 'OK',
  onClose,
}) => {
  const [isTyping, setIsTyping] = useState(false);

  const soundIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isTyping) {
      soundIntervalRef.current = setInterval(() => {
        playSound('text/one');
      }, textSpeed);
    } else {
      if (soundIntervalRef.current) {
        clearInterval(soundIntervalRef.current);
      }
    }

    return (): void => {
      if (soundIntervalRef.current) clearInterval(soundIntervalRef.current);
    };
  }, [isTyping]);

  const characterConfig = {
    text,
    buttonText: 'OK',
    character: tutorialCharacter,
  } as IntroStepsConfigValue;

  return (
    <div className={cn(overlayCss.overlay)}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <CharacterPicture config={characterConfig} skipFinishAnimation />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        style={{
          width: 'calc(100% - 2rem)',
          top: '70%',
        }}
        className="absolute left-[60%] -translate-x-1/2 z-[1]"
      >
        <div className={cn(stepCss.step)}>
          <div className="relative flex flex-col gap-[10px]">
            {!!title && (
              <span className="!text-main-bg text-[16px] font-medium leading-1.2 invisible whitespace-pre-line bold">
                {title}
              </span>
            )}
            <span className="!text-main-bg text-[16px] font-medium leading-1.2 invisible whitespace-pre-line">
              {text}
            </span>
            {!!title && (
              <TypeAnimation
                key={`tutorial-title-${text}`}
                sequence={[title]}
                wrapper="span"
                cursor={false}
                speed={textSpeed}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '700',
                  color: 'rgb(24 26 32)',
                  marginBottom: '0.5rem',
                  lineHeight: '120%',
                  whiteSpace: 'pre-line',
                }}
              />
            )}
            <TypeAnimation
              key={`tutorial-text-${text}`}
              sequence={[
                (): void => setIsTyping(true),
                text,
                (): void => setIsTyping(false),
              ]}
              wrapper="span"
              cursor={false}
              speed={textSpeed}
              style={{
                position: 'absolute',
                top: title ? '25px' : 0,
                left: 0,
                display: 'block',
                fontSize: '16px',
                fontWeight: '500',
                color: 'rgb(24 26 32)',
                lineHeight: '120%',
                whiteSpace: 'pre-line',
              }}
            />
          </div>
          <RoundedButton
            onClick={onClose}
            className="absolute right-[1rem] bottom-0 translate-y-1/2 pointer-events-auto"
            text={buttonText}
          />
        </div>
      </motion.div>
    </div>
  );
};

export { CharacterDialog };
